import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CierreInactividadConfig } from '../../models/cierre-inactividad-config.model';
import { CierreMenuInactividadConfig } from '../../models/cierre-menu-inactividad-config.model';
import { PresenciaInactividadConfig } from '../../models/presencia-inactividad-config.model';
import { TicketIntegrationConfig, TicketIntegrationRuntimeConfig, TicketIntegrationStatusResponse } from '../../models/ticket-integration.model';
import { GestionWebhookStatusResponse } from '../../models/gestion-webhook-status.model';
import { InternalAttachmentPolicy } from '../../models/internal-attachment.model';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
   private apiUrl: string = environment.apiUrl;
   private readonly ticketIntegrationEnabledState = signal<boolean | null>(null);
   private readonly ticketWebBaseUrlState = signal<string | null>(null);
   readonly ticketIntegrationEnabled = this.ticketIntegrationEnabledState.asReadonly();
   readonly ticketWebBaseUrl = this.ticketWebBaseUrlState.asReadonly();

  constructor(private http: HttpClient) { }

  getInternalAttachmentPolicy(): Observable<InternalAttachmentPolicy> {
    return this.http.get<InternalAttachmentPolicy>(`${this.apiUrl}/configuracion/datos/adjuntos-internos`);
  }
  saveInternalAttachmentPolicy(policy: Pick<InternalAttachmentPolicy, 'maxSizeMb' | 'maxFiles' | 'enabledFormats'>): Observable<InternalAttachmentPolicy> {
    return this.http.put<InternalAttachmentPolicy>(`${this.apiUrl}/configuracion/datos/adjuntos-internos`, policy);
  }

  getTicketIntegrationConfig(): Observable<TicketIntegrationConfig> {
    return this.http.get<TicketIntegrationConfig>(`${this.apiUrl}/configuracion/tickets`).pipe(
      tap(config => this.ticketIntegrationEnabledState.set(config.enabled)),
      catchError(error => {
        this.ticketIntegrationEnabledState.set(null);
        return throwError(() => error);
      }),
    );
  }

  getTicketIntegrationEnabled(): Observable<TicketIntegrationRuntimeConfig> {
    return this.http.get<TicketIntegrationRuntimeConfig>(`${this.apiUrl}/chat/tickets/integration-enabled`).pipe(
      tap(config => {
        this.ticketIntegrationEnabledState.set(config.enabled);
        this.ticketWebBaseUrlState.set(config.ticketWebBaseUrl || null);
      }),
      catchError(error => {
        this.ticketIntegrationEnabledState.set(null);
        this.ticketWebBaseUrlState.set(null);
        return throwError(() => error);
      }),
    );
  }

  saveTicketIntegrationConfig(enabled: boolean, baseUrl: string): Observable<TicketIntegrationConfig> {
    return this.http.put<TicketIntegrationConfig>(`${this.apiUrl}/configuracion/tickets`, { enabled, baseUrl }).pipe(
      tap(config => this.ticketIntegrationEnabledState.set(config.enabled)),
      catchError(error => {
        this.ticketIntegrationEnabledState.set(null);
        return throwError(() => error);
      }),
    );
  }

  getTicketIntegrationStatus(): Observable<TicketIntegrationStatusResponse> {
    return this.http.get<TicketIntegrationStatusResponse>(`${this.apiUrl}/configuracion/tickets/status`);
  }

  getGestionWebhookStatus(): Observable<GestionWebhookStatusResponse> {
    return this.http.get<GestionWebhookStatusResponse>(`${this.apiUrl}/configuracion/gestion-webhook/status`);
  }

  getCierreInactividadConfig(): Observable<CierreInactividadConfig> {
    return this.http.get<CierreInactividadConfig>(`${this.apiUrl}/configuracion/cierre-inactividad`);
  }

  guardarCierreInactividadConfig(config: CierreInactividadConfig): Observable<CierreInactividadConfig> {
    return this.http.post<CierreInactividadConfig>(`${this.apiUrl}/configuracion/cierre-inactividad`, config);
  }

  getCierreMenuInactividadConfig(): Observable<CierreMenuInactividadConfig> {
    return this.http.get<CierreMenuInactividadConfig>(`${this.apiUrl}/configuracion/cierre-menu-inactividad`);
  }

  guardarCierreMenuInactividadConfig(config: CierreMenuInactividadConfig): Observable<CierreMenuInactividadConfig> {
    return this.http.post<CierreMenuInactividadConfig>(
      `${this.apiUrl}/configuracion/cierre-menu-inactividad`,
      config,
    );
  }

  getPresenciaInactividadConfig(): Observable<PresenciaInactividadConfig> {
    return this.http.get<PresenciaInactividadConfig>(`${this.apiUrl}/configuracion/presencia-inactividad`);
  }

  guardarPresenciaInactividadConfig(
    config: PresenciaInactividadConfig,
  ): Observable<PresenciaInactividadConfig> {
    return this.http.put<PresenciaInactividadConfig>(
      `${this.apiUrl}/configuracion/presencia-inactividad`,
      config,
    );
  }

  getTiposDatos(): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/tipodatos/obtenerTiposDatos`);
  }

  guardarTiposDatos(tiposDatos: any[]): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/tipodatos/guardarTiposDatos`,
      tiposDatos,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
        responseType: 'text' as 'json'
      }
    );
  }

  getAreas(): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/areas/obtenerAreas`);
  }

  guardarArea(area: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/areas/guardarArea`,
      area,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
        responseType: 'text' as 'json'
      }
    );
  }
guardarAreaEditada(area: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/areas/editarArea`,
      area,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
        responseType: 'text' as 'json'
      }
    );
  }

  eliminarArea(area: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/areas/borrarArea`,
      area,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
        responseType: 'text' as 'json'
      }
    );
  }


  getMensajesPredefinidos(): Observable<any> {
    const params = new HttpParams().set('_ts', Date.now().toString());
    return this.http.get(
      `${this.apiUrl}/mensajesPredefinidos/obtenerMensajesPredefinidos`,
      {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
  }

  guardarMensajesPredefinidos(mensaje: any): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/mensajesPredefinidos/guardarMensajesPredefinidos`,
      { id: mensaje.id, cuerpo: mensaje.cuerpo },
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
        responseType: 'text' as 'json'
      }
    );
  }

  agregarMensajePredefinido(mensaje: { descripcion: string; cuerpo: string }): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/mensajesPredefinidos/agregarMensajesPredefinidos`,
      mensaje,
      {
        headers: { 'Content-Type': 'application/json' },
        observe: 'response',
      }
    );
  }
}
